import React, { ReactNode } from 'react';

import { Box, ChakraStyledOptions, Flex, Grid, Heading, Hide, Show, Text } from '@chakra-ui/react';

import { TimelineSectionItem } from '@/components/sections/services/sections';
import { TimelineSectionItem as TimelineSectionItemType } from '@/components/sections/services/typings';
import { AnimateWhenInViewWrapper, SectionContentWrapper, SectionWrapper } from '@/components/wrappers';

export type TimelineSectionProps = {
  items: TimelineSectionItemType[];
  header?: ReactNode;
  gridColumns?: number;
  itemStyle?: ChakraStyledOptions;
};

export const TimelineSection = ({ items, header }: TimelineSectionProps) => {
  return (
    <SectionWrapper backgroundColor="surface.500" overflow="hidden">
      <SectionContentWrapper display="flex" flexDir="column">
        <Box>
          {header && (
            <Heading
              variant={{
                base: 'component-heading-large/xl',
                xs: 'component-heading-large/2xl',
                sm: 'component-heading-large/3xl',
              }}
              color="white"
              mt={10}
              mb={20}
            >
              {header}
            </Heading>
          )}
        </Box>

        <Flex
          justifyContent={{
            base: 'start',
            xl: 'center',
          }}
          mb={10}
        >
          <Hide above="lg">
            <Flex flexDir="column" alignItems="center" w="100%" maxW="600px" mx="auto">
              {items.map((item, index) => {
                const isLastPoint = index === items.length - 1;

                return (
                  <Flex pb={8} key={`timeline-point-${item.number}`} position="relative">
                    <Flex flexDir="column" alignItems="center" mr={4} position="relative">
                      <Box
                        w="12px"
                        h="12px"
                        borderRadius="50%"
                        bgColor="white"
                        mt="4px"
                        zIndex={2}
                        position="relative"
                      />
                      <Box
                        w="1px"
                        position="absolute"
                        top="10px"
                        bottom="-36px"
                        left="50%"
                        transform="translateX(-50%)"
                        sx={
                          isLastPoint
                            ? {
                                background: 'linear-gradient(180deg, #FFFFFF 82.69%, rgba(255, 255, 255, 0) 100%)',
                              }
                            : {}
                        }
                        bgColor={isLastPoint ? 'transparent' : 'white'}
                      />
                    </Flex>

                    <AnimateWhenInViewWrapper>
                      <Flex flexDir="column">
                        <Text variant="text-xl/lineHeight-8/font-medium" color="text.500" mb={1}>
                          {item.header}
                        </Text>
                        <Text maxW="362px" variant="text-md/lineHeight-6/font-normal" color="text.300">
                          {item.description}
                        </Text>
                      </Flex>
                    </AnimateWhenInViewWrapper>
                  </Flex>
                );
              })}
            </Flex>
          </Hide>

          <Show above="lg">
            <Grid
              columnGap={{
                base: 10,
                md: 12,
                lg: 16,
              }}
              rowGap={{
                base: 10,
                md: 14,
                lg: 20,
              }}
              maxW={{
                base: '1200px',
                xl: 'unset',
              }}
              gridTemplateColumns={{
                lg: `repeat(${Math.min(items.length, 6)}, 1fr)`,
                xl: `repeat(${Math.min(items.length, 6)}, 1fr)`,
              }}
            >
              {items.map((item, index) => (
                <AnimateWhenInViewWrapper key={`timeline-section-item-${item.number}`}>
                  <TimelineSectionItem {...item} isLast={index === items.length - 1} index={index} />
                </AnimateWhenInViewWrapper>
              ))}
            </Grid>
          </Show>
        </Flex>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
